/* ==========================================================================
   INSHINE / Focal: High-Speed Instant Quick-Buy & Cart Drawer Engine
   ========================================================================== */

(function () {
  'use strict';
  
  const quickBuyHtmlCache = new Map();
  const preloadedImages = new Set();
  let isAddingToCart = false;

  // --------------------------------------------------------------------------
  // 1. IN-MEMORY BACKGROUND PRE-CACHER (0ms Modal Opening)
  // --------------------------------------------------------------------------
  function preloadImage(url) {
    if (!url || preloadedImages.has(url)) return;
    preloadedImages.add(url);
    const img = new Image();
    img.src = url;
  }

  async function cacheQuickBuyData(productUrl) {
    if (!productUrl) return;
    const cleanUrl = productUrl.split('?')[0];
    const fetchUrl = cleanUrl.includes('?') ? `${cleanUrl}&view=quick-buy` : `${cleanUrl}?view=quick-buy`;

    if (quickBuyHtmlCache.has(fetchUrl) || quickBuyHtmlCache.has(cleanUrl)) return;

    try {
      const response = await fetch(fetchUrl, { priority: 'low' });
      if (response.ok) {
        const htmlText = await response.text();
        quickBuyHtmlCache.set(fetchUrl, htmlText);
        quickBuyHtmlCache.set(cleanUrl, htmlText);

        // Parse and pre-warm the primary shade images into GPU cache
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, 'text/html');
        doc.querySelectorAll('img[src], img[srcset]').forEach((img) => {
          if (img.src) preloadImage(img.src);
        });
      }
    } catch (e) {
      // Fail silently if offline or blocked
    }
  }

  // Pre-cache cards as soon as they enter the screen
  const cardObserver = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const card = entry.target;
          const link = card.querySelector('a[href*="/products/"]');
          if (link && link.href) {
            cacheQuickBuyData(link.href);
          }
          observer.unobserve(card);
        }
      });
    },
    { rootMargin: '300px 0px' } // Pre-loads 300px before user scrolls to the card
  );

  function observeAllProductCards() {
    document.querySelectorAll('product-item, .product-item, [data-product-id]').forEach((card) => {
      cardObserver.observe(card);
    });
  }

  // --------------------------------------------------------------------------
  // 2. FETCH INTERCEPTOR (Serves Quick-Buy Instantly From RAM)
  // --------------------------------------------------------------------------
  const nativeFetch = window.fetch;
  window.fetch = async function (...args) {
    const rawUrl = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';

    // If Focal theme is requesting quick-buy HTML and we have it cached, return 0ms response
    if (rawUrl.includes('view=quick-buy') || rawUrl.includes('/products/')) {
      const cachedHtml = quickBuyHtmlCache.get(rawUrl) || quickBuyHtmlCache.get(rawUrl.split('?')[0]);
      if (cachedHtml) {
        return new Response(cachedHtml, {
          status: 200,
          headers: { 'Content-Type': 'text/html; charset=utf-8' }
        });
      }
    }

    // Standard AJAX Add-to-Cart interceptor
    const response = await nativeFetch.apply(this, args);
    if (rawUrl.includes('/cart/add') && response.ok) {
      closeShadeModal();
      requestAnimationFrame(() => {
        openMiniCartDrawer();
        isAddingToCart = false;
      });
    }
    return response;
  };

  // --------------------------------------------------------------------------
  // 3. MODAL DISMISSAL & CART DRAWER HANDLERS
  // --------------------------------------------------------------------------
  function closeShadeModal() {
    const quickElements = document.querySelectorAll(
      'quick-buy-drawer, quick-buy-popover, [id*="quick-buy"], [id*="QuickBuy"], .quick-buy, .popover'
    );

    quickElements.forEach((el) => {
      el.open = false;
      el.removeAttribute('open');
      el.setAttribute('aria-hidden', 'true');
      el.classList.remove('is-open', 'active');
      if (typeof el.close === 'function') el.close();
    });

    document.querySelectorAll('.drawer__overlay, .popover__overlay, .modal__overlay').forEach((overlay) => {
      overlay.classList.remove('is-open', 'active');
    });
    document.documentElement.classList.remove('has-modal-open', 'is-locked');
    document.body.classList.remove('has-modal-open', 'is-locked', 'is-quick-buy-open');
  }

  function openMiniCartDrawer() {
    const miniCart = document.querySelector('mini-cart, #mini-cart, cart-drawer');
    if (miniCart) {
      if (typeof miniCart.open === 'boolean') miniCart.open = true;
      miniCart.setAttribute('open', '');
      miniCart.setAttribute('aria-hidden', 'false');
      if (typeof miniCart.show === 'function') miniCart.show();
    }

    document.documentElement.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));

    const cartButton = document.querySelector('a[href="/cart"], [aria-controls="mini-cart"], [data-action="open-mini-cart"]');
    if (cartButton && (!miniCart || !miniCart.hasAttribute('open'))) {
      cartButton.click();
    }
  }

  // --------------------------------------------------------------------------
  // 4. EVENT LISTENERS
  // --------------------------------------------------------------------------
  document.addEventListener('DOMContentLoaded', () => {
    observeAllProductCards();

    // Sync mobile grey backdrop overlay
    const quickBuyObserver = new MutationObserver(() => {
      const isOpen = Boolean(document.querySelector('quick-buy-drawer[open], quick-buy-popover[open], [id*="quick-buy"][open]'));
      document.body.classList.toggle('is-quick-buy-open', isOpen);
    });
    quickBuyObserver.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['open'] });
  });

  // Re-observe cards when filters change or pagination loads more items
  document.documentElement.addEventListener('facet:refreshed', observeAllProductCards);
  document.documentElement.addEventListener('collection:reloaded', observeAllProductCards);

  // Fast pre-cache on hover/touch before click
  document.addEventListener('mouseover', (e) => {
    const card = e.target.closest('product-item, .product-item');
    if (card) {
      const link = card.querySelector('a[href*="/products/"]');
      if (link) cacheQuickBuyData(link.href);
    }
  }, { passive: true });

  // Add to cart click trigger
  document.addEventListener('click', (event) => {
    const addBtn = event.target.closest('button[type="submit"][name="add"], .product-form__submit, [data-action="add-to-cart"]');
    if (!addBtn) return;

    isAddingToCart = true;
    const insideQuickBuy = addBtn.closest('quick-buy-drawer, quick-buy-popover, [id*="quick-buy"], [id*="QuickBuy"], .popover');
    if (insideQuickBuy) closeShadeModal();
  }, true);

  document.documentElement.addEventListener('cart:refresh', () => {
    if (isAddingToCart) {
      closeShadeModal();
      requestAnimationFrame(openMiniCartDrawer);
      isAddingToCart = false;
    }
  });
})();
